import {
    findFocusedRoute,
    useNavigation,
    useRoute
} from '@react-navigation/native';
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { doCheckImportVisit } from '../../combo';
import {
    LoadingView,
    Page,
    Platform,
    ScrollView,
    View
} from '../../common';
import { usePersonalData } from '../../hooks/usePersonalData';
import { syncUserState } from '../../init';
import { getUserLoadingState } from '../../selectors';
import {
    getFamilyWithoutConsent,
    getFamilyWithoutConsentObject,
    isProfileWithoutConsent
} from '../../selectors/consents';
import { eventBus } from '../../services/eventBus';
// import { hasProfilesWithoutConsents } from '../../utils/consentsUtils';
import { renderRelativePanel } from '../Account/snippets.js';
import { StyledAgreementCheckbox } from '../Visits/snippets/NewVisitModal/snippets.js';
import { ChildConsent } from './ChildConsent';
import { MainUserConsent } from './MainUserConsent';

const resetToAccountScreen = (navigation, screenName, screenParams) => {
    const rootState = navigation.getRootState?.();
    const focused = rootState ? findFocusedRoute(rootState) : null;

    if (focused?.name === screenName) {
        return;
    }

    // Close ConsentConfirmation first so it does not remain on top of the stack. Don't use reset to avoid losing nested history in the Account stack,
    // which is critical for the booking flow.
    navigation.goBack();

    // Then navigate into the Account stack without resetting nested history.
    navigation.navigate('Account', {
        screen: screenName,
        params: screenParams,
    });
};

const ProfileItem = renderRelativePanel(false);

export const ProfilesListItem = ({
    item,
    onSelect,
}) => {
    const [ isSelected, setIsSelected ] = useState(false);
    const onPress = () => {
        setIsSelected(!isSelected);
        onSelect({
            item,
            isSelected: !isSelected,
        });
    };
    return (
        <View style={{ marginBottom: 16 }}>
            {item?.name && item?.birthday ? (
                <ProfileItem
                    item={item}
                    withEditBadge={false}
                    readOnly
                />
            ) : null}
            <StyledAgreementCheckbox
                title={
                    item?.isMainProfile
                        ? Object.R('titles.personalDataAgreement')
                        : Object.R('titles.childDataAgreement')
                }
                selected={isSelected}
                onPress={onPress}
            />
        </View>
    );
};

export const ConsentConfirmation = () => {
    const {
        consents,
        isLoading,
    } = usePersonalData();

    const { params } = useRoute() ?? {};

    const familyWithoutConsent = useSelector(getFamilyWithoutConsent);
    const familyWithoutConsentObject = useSelector(getFamilyWithoutConsentObject);
    const userWithoutConsent = useSelector(isProfileWithoutConsent);
    const isUserLoading = useSelector(getUserLoadingState);
    const safeArea = useSafeAreaInsets();
    const canLeave = useRef(false);
    /** Ensures exit navigation runs once per visit. The ref resets on remount. */
    const exitNavigationDoneRef = useRef(false);
    const navigation = useNavigation();
    const hasChildWithoutConsent = Object.keys(familyWithoutConsentObject?.clinics ?? {}).length > 0;
    const hasPendingConsents = userWithoutConsent || hasChildWithoutConsent;

    const performExitNavigation = useCallback(() => {
        if (exitNavigationDoneRef.current) {
            return;
        }

        exitNavigationDoneRef.current = true;

        if (Object.isAddProgram && Object.newProgram) {
            setTimeout(() => {
                eventBus.emit('onNewUserItemsFound', {newProgram: Object.newProgram, onRequestClose: Object.onRequestCloseNewProgram, onClose: Object.onCloseNewProgram});
            }, 1000);
        } else {
            doCheckImportVisit('ConsentConfirmation');
            Object.ConsentConfirmed = true;
        }

        if (params?.to) {
            resetToAccountScreen(navigation, params.to, Object.preEnrollVisitParams);
        } else {
            navigation.goBack();
        }
    }, [ navigation, params?.to ]);

    useEffect(() => {
        if (isLoading || isUserLoading || hasPendingConsents) {
            return;
        }

        canLeave.current = true;
        Object.profilesWithoutConsent = null;

        performExitNavigation();
    }, [
        hasPendingConsents,
        isLoading,
        isUserLoading,
        performExitNavigation,
        userWithoutConsent,
        familyWithoutConsent,
    ]);

    const handleConsentUpdateComplete = useCallback((completedProfileId, profileType = 'unknown') => {
        // Check if there are any more profiles without consents, excluding the one that just completed
        let hasRemainingProfiles = false;

        // Check user profile
        if (consents?.user) {
            if (profileType !== 'user' && userWithoutConsent) {
                hasRemainingProfiles = true;
            }
        }

        if (!hasRemainingProfiles && consents?.family && typeof consents.family === 'object') {
            hasRemainingProfiles = Object.entries(consents.family)
                .some(([ profileId ]) => {
                    const isNotCompletedProfile = profileId !== completedProfileId;
                    return isNotCompletedProfile && familyWithoutConsent?.includes(profileId);
                });
        }

        // Only reset if there are no more profiles that need consent
        if (!hasRemainingProfiles) {
            canLeave.current = true;
            Object.profilesWithoutConsent = null;
            // back({ toTop: true });
            // navigation.navigate('Home');
            setTimeout(() => syncUserState(() => {
                performExitNavigation();
            }, undefined, undefined, true), 0);

        }
    }, [consents.family,
        consents?.user,
        familyWithoutConsent,
        userWithoutConsent,
        performExitNavigation ]);

    let content = null;
    if (userWithoutConsent) {
        content = <MainUserConsent onConsentUpdateComplete={(profileId) => handleConsentUpdateComplete(profileId, 'user')}/>;
    } else if (hasChildWithoutConsent) {
        content = <ChildConsent
            profileId={Object.keys(familyWithoutConsentObject?.clinics)?.[0]}
            onConsentUpdateComplete={(profileId, offlineClinics) => {
                handleConsentUpdateComplete(profileId, 'child', offlineClinics);
            }}
        />;
    }

    if (isUserLoading) {
        return <LoadingView title={'titles.profileUpdatingHint'}/>;
    }

    return (
        <Page
            safeArea={safeArea}
            noscroll
            isLoading={isLoading || isUserLoading}>
            <ScrollView
                enableOnAndroid
                enableAutomaticScroll
                accessibilityLabel="scroll"
                extraScrollHeight={Platform.OS === 'ios' ? 60 : 10}
                style={{
                    height: Platform.OS === 'web' ? 0 : undefined,
                    flex: 1,
                }}
                contentContainerStyle={{
                    flexGrow: 1,
                    marginHorizontal: 12,
                    justifyContent: 'space-between',
                }}
            >
                {content}
            </ScrollView>
        </Page>
    );
};
